import React from "react";
import { Link, useParams } from "react-router-dom";
import { makeAPI } from "../services/api";
import FullscreenController from "../utils/class.fullscreen_manager";

function buildGameImage(game) {
    if (!game?.assets?.length) return "";
    const rectangularSizes = ["1280x720", "960x540", "800x450", "640x360"];
    const preferredAsset =
        rectangularSizes
            .map((size) => game.assets.find((asset) => typeof asset === "string" && asset.includes(size)))
            .find(Boolean) ||
        game.assets.find((asset) => typeof asset === "string" && asset.includes("512x512")) ||
        game.assets.find((asset) => typeof asset === "string") ||
        "";

    return preferredAsset ? `https://img.gamedistribution.com/${preferredAsset}` : "";
}

function slugifyGame(game) {
    const base = (game?.slug || game?.title_slug || game?.title || "game")
        .toString()
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

    return base || "game";
}

function inferOrientation(game) {
    // if (!game?.width || !game?.height) return "landscape";
    // return Number(game.width) >= Number(game.height) ? "landscape" : "portrait";
    console.log("Inferring orientation for game:", game?.title, "with mobile_mode:", game?.mobile_mode);
    if (!game?.mobile_mode ==="Landscape") return "landscape";
    if (!game?.mobile_mode ==="Portrait") return "portrait";
    return "landscape";
    
}

function getStoredUser() {
    try {
        return JSON.parse(localStorage.getItem("user") || "null");
    } catch (error) {
        console.error("Error reading local user:", error);
        return null;
    }
}

function getGuestKey() {
    const existingKey = localStorage.getItem("game_feedback_guest_key");
    if (existingKey) return existingKey;

    const nextKey = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("game_feedback_guest_key", nextKey);
    return nextKey;
}

export default function GamePlayer() {
    const { slug } = useParams();
    const fullscreenRef = React.useRef(null);
    const { current: playerIds } = React.useRef({
        container: "iframe-div",
        iframe: "game_frame",
        image: "img_frame",
        start: "start-btn",
        floatingExit: "float_button",
        rotateOverlay: "rotate-overlay"
    });

    const [game, setGame] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [similarGames, setSimilarGames] = React.useState([]);
    const [similarLoading, setSimilarLoading] = React.useState(false);
    const [feedbackSummary, setFeedbackSummary] = React.useState({ like_count: 0, dislike_count: 0, user_reaction: "" });
    const [feedbackLoading, setFeedbackLoading] = React.useState(false);
    const [feedbackBusy, setFeedbackBusy] = React.useState("");
    const [feedbackMessage, setFeedbackMessage] = React.useState("");
    const [isReportOpen, setIsReportOpen] = React.useState(false);
    const [reportForm, setReportForm] = React.useState({ reason: "bug", details: "" });
    const [reportSubmitting, setReportSubmitting] = React.useState(false);
    const [isMobileOrTablet, setIsMobileOrTablet] = React.useState(() => window.innerWidth <= 1199);
    const [started, setStarted] = React.useState(false);
    const [pendingFullscreen, setPendingFullscreen] = React.useState(false);

    const restoreMobilePreview = React.useCallback(() => {
        const container = document.getElementById(playerIds.container);
        const floatingExit = document.getElementById(playerIds.floatingExit);
        const image = document.getElementById(playerIds.image);
        const startButton = document.getElementById(playerIds.start);

        if (container) {
            container.classList.remove("fullscreen_manager");
            container.style.removeProperty("display");
            container.style.removeProperty("height");
            container.style.removeProperty("width");
        }

        if (floatingExit) {
            floatingExit.style.removeProperty("display");
            floatingExit.classList.remove("float_button_visibility");
        }

        if (image) image.style.removeProperty("display");
        if (startButton) startButton.style.removeProperty("display");
    }, [playerIds.container, playerIds.floatingExit, playerIds.image, playerIds.start]);

    React.useEffect(() => {
        document.title = "Game Player - CGES.CLUB";
        fullscreenRef.current = new FullscreenController();

        const handleFullscreenChange = () => {
            if (!document.fullscreenElement && !document.webkitFullscreenElement) {
                fullscreenRef.current?.ExitFullscreen();
                if (window.innerWidth <= 1199) {
                    setStarted(false);
                    setPendingFullscreen(false);
                    window.requestAnimationFrame(() => restoreMobilePreview());
                }
            }
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);
        document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

        return () => {
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
            document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
            fullscreenRef.current?.ExitFullscreen({ forceExit: true });
        };
    }, [restoreMobilePreview]);

    React.useEffect(() => {
        const handleResize = () => {
            setIsMobileOrTablet(window.innerWidth <= 1199);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const resolvedSlug = React.useMemo(() => (slug || "").trim(), [slug]);

    React.useEffect(() => {
        const loadGame = async () => {
            setLoading(true);
            setStarted(false);
            setPendingFullscreen(false);

            try {
                let response = await makeAPI("/api/get_game_info/detail", "POST", false, { slug: resolvedSlug });

                if (response.status !== "S") {
                    response = await makeAPI("/api/get_game_info/detail", "POST", false, { title_slug: resolvedSlug });
                }

                if (response.status === "S") {
                    setGame(response.game);
                    document.title = `${response.game.title} - CGES.CLUB`;
                } else {
                    setGame(null);
                }
            } catch (error) {
                console.error("Error loading game detail:", error);
                setGame(null);
            } finally {
                setLoading(false);
            }
        };

        loadGame();
    }, [resolvedSlug]);

    const preview = buildGameImage(game);
    const orientation = React.useMemo(() => inferOrientation(game), [game]);

    const openFullscreen = React.useCallback(() => {
        const controller = fullscreenRef.current;
        if (!controller || !game?.embed) return;

        controller.refreshElements?.();
        const os = controller.getOS?.() || "Unknown";

        if (os === "Unknown") {
            controller.EnterFullscreen(orientation);
        } else {
            controller.func_int({ orientation });
        }
    }, [game?.embed, orientation]);

    React.useEffect(() => {
        if (!started || !pendingFullscreen || !game?.embed) return undefined;

        const timeout = setTimeout(() => {
            openFullscreen();
            setPendingFullscreen(false);
        }, 150);

        return () => clearTimeout(timeout);
    }, [game?.embed, openFullscreen, pendingFullscreen, started]);

    React.useEffect(() => {
        if (!game?.embed) return;
        setStarted(!isMobileOrTablet);
        setPendingFullscreen(false);
    }, [game?.embed, isMobileOrTablet]);

    React.useEffect(() => {
        if (!started && isMobileOrTablet) {
            restoreMobilePreview();
        }
    }, [isMobileOrTablet, restoreMobilePreview, started]);

    React.useEffect(() => {
        if (!game?.game_id) {
            setFeedbackSummary({ like_count: 0, dislike_count: 0, user_reaction: "" });
            setFeedbackLoading(false);
            return;
        }

        const loadFeedbackSummary = async () => {
            setFeedbackLoading(true);
            try {
                const localUser = getStoredUser();
                const response = await makeAPI("/api/game_feedback/summary", "POST", false, {
                    game_id: game.game_id,
                    user_id: Number(localUser?.user_id) || 0,
                    guest_key: getGuestKey()
                });

                if (response.status === "S") {
                    setFeedbackSummary({
                        like_count: Number(response.like_count) || 0,
                        dislike_count: Number(response.dislike_count) || 0,
                        user_reaction: response.user_reaction || ""
                    });
                }
            } catch (error) {
                console.error("Error loading game feedback summary:", error);
            } finally {
                setFeedbackLoading(false);
            }
        };

        loadFeedbackSummary();
    }, [game?.game_id]);

    React.useEffect(() => {
        const loadSimilarGames = async () => {
            if (!game?.game_id) {
                setSimilarGames([]);
                setSimilarLoading(false);
                return;
            }

            const categorySlugs = Array.isArray(game.category_slugs) ? game.category_slugs.filter(Boolean) : [];
            const tagSlugs = Array.isArray(game.tag_slugs) ? game.tag_slugs.filter(Boolean) : [];
            const requests = [];

            if (categorySlugs.length || tagSlugs.length) {
                if (categorySlugs.length && tagSlugs.length) {
                    requests.push({ category_slugs: categorySlugs, tag_slugs: tagSlugs });
                }
                if (categorySlugs.length) {
                    requests.push({ category_slugs: categorySlugs, tag_slugs: [] });
                }
                if (tagSlugs.length) {
                    requests.push({ category_slugs: [], tag_slugs: tagSlugs });
                }
            }

            if (!requests.length) {
                setSimilarGames([]);
                setSimilarLoading(false);
                return;
            }

            setSimilarLoading(true);

            try {
                const seen = new Set();
                const merged = [];

                for (const filter_data of requests) {
                    const response = await makeAPI("/api/get_game_info/catalog", "POST", false, {
                        page: 1,
                        limit: 12,
                        filter_data: {
                            ...filter_data,
                            search: "",
                            mobile_mode: "",
                            sortby: "popular"
                        }
                    });

                    if (response.status !== "S") continue;

                    for (const item of response.games || []) {
                        if (!item || item.game_id === game.game_id || seen.has(item.game_id)) continue;
                        seen.add(item.game_id);
                        merged.push(item);
                        if (merged.length >= 24) break;
                    }

                    if (merged.length >= 24) break;
                }

                setSimilarGames(merged);
            } catch (error) {
                console.error("Error loading similar games:", error);
                setSimilarGames([]);
            } finally {
                setSimilarLoading(false);
            }
        };

        loadSimilarGames();
    }, [game]);

    const handleMobileLaunch = () => {
        setStarted(true);
        setPendingFullscreen(true);
    };

    const handleEnterFullscreen = () => {
        if (!started) {
            setStarted(true);
            setPendingFullscreen(true);
            return;
        }

        openFullscreen();
    };

    const handleExitFullscreen = () => {
        const controller = fullscreenRef.current;

        controller?.ExitFullscreen({ forceExit: true });
        if (isMobileOrTablet) {
            setStarted(false);
            setPendingFullscreen(false);
            window.requestAnimationFrame(() => restoreMobilePreview());
        }
    };

    const handleReload = () => {
        if (!game?.embed) return;
        if (isMobileOrTablet) {
            setStarted(false);
            setPendingFullscreen(false);
            return;
        }
        setStarted(false);
        window.requestAnimationFrame(() => setStarted(true));
    };

    const handleReaction = React.useCallback(async (reaction) => {
        if (!game?.game_id) return;

        const localUser = getStoredUser();

        setFeedbackBusy(reaction);
        setFeedbackMessage("");

        try {
            const response = await makeAPI("/api/game_feedback/react", "POST", false, {
                game_id: game.game_id,
                user_id: Number(localUser?.user_id) || 0,
                guest_key: getGuestKey(),
                reaction
            });

            if (response.status === "S") {
                setFeedbackSummary({
                    like_count: Number(response.like_count) || 0,
                    dislike_count: Number(response.dislike_count) || 0,
                    user_reaction: response.user_reaction || reaction
                });
                setFeedbackMessage("Thanks for the feedback.");
            } else {
                setFeedbackMessage(response.message || "Unable to save feedback.");
            }
        } catch (error) {
            console.error("Error saving game reaction:", error);
            setFeedbackMessage("Unable to save feedback right now.");
        } finally {
            setFeedbackBusy("");
        }
    }, [game?.game_id]);

    const handleOpenReport = React.useCallback(() => {
        setFeedbackMessage("");
        setIsReportOpen(true);
    }, []);

    const handleSubmitReport = React.useCallback(async (event) => {
        event.preventDefault();

        if (!game?.game_id || !reportForm.reason) return;

        setReportSubmitting(true);
        setFeedbackMessage("");

        try {
            const localUser = getStoredUser();
            const response = await makeAPI("/api/game_feedback/report", "POST", false, {
                game_id: game.game_id,
                user_id: Number(localUser?.user_id) || 0,
                guest_key: getGuestKey(),
                game_title: game.title,
                reason: reportForm.reason,
                details: reportForm.details.trim(),
                page_url: window.location.href
            });

            if (response.status === "S") {
                setIsReportOpen(false);
                setReportForm({ reason: "bug", details: "" });
                setFeedbackMessage("Report submitted. Thanks for helping us improve this game.");
            } else {
                setFeedbackMessage(response.message || "Unable to submit report.");
            }
        } catch (error) {
            console.error("Error submitting game report:", error);
            setFeedbackMessage("Unable to submit report right now.");
        } finally {
            setReportSubmitting(false);
        }
    }, [game?.game_id, game?.title, reportForm]);

    return (
        <>
            <style>{`
                .gp-page{--page:#000;--panel:#111;--soft:#1c1c1c;--soft2:#0f0f0f;--border:rgba(255,255,255,.08);--text:#f6f6f6;--muted:#9d9d9d;--accent:#f46119;--accent2:#ff7a33;color:var(--text);padding:12px 0 28px;background:#000}
                .gp-shell{display:flex;flex-direction:column;gap:20px}
                .gp-top{display:flex;justify-content:space-between;align-items:center;gap:14px}
                .gp-link{display:inline-flex;align-items:center;gap:8px;padding:11px 14px;border-radius:999px;background:#111;border:1px solid var(--border);color:#fff;text-decoration:none;font-weight:700}
                .gp-link:hover{color:#fff;text-decoration:none}
                .gp-kicker{margin:0;color:var(--muted);font-size:13px;letter-spacing:.08em;text-transform:uppercase}
                .gp-layout{display:grid;grid-template-columns:minmax(0,960px) minmax(360px,1fr);gap:22px;align-items:start}
                .gp-main{display:flex;flex-direction:column;gap:18px;min-width:0}
                .gp-panel{background:var(--panel);border:1px solid var(--border);border-radius:18px;box-shadow:0 24px 60px rgba(0,0,0,.28)}
                .gp-stage{padding:16px}
                .gp-stage-head{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;margin-bottom:14px}
                .gp-title{margin:0;font-size:32px;font-weight:800;letter-spacing:-.03em;color:#fff}
                .gp-meta{display:flex;flex-wrap:wrap;gap:8px;margin-top:10px}
                .gp-pill{display:inline-flex;align-items:center;min-height:34px;padding:0 12px;border-radius:999px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.06);color:#d8d8d8;font-size:12px;font-weight:700}
                .gp-actions{display:flex;flex-wrap:wrap;gap:10px;justify-content:flex-end}
                .gp-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:44px;padding:0 16px;border-radius:999px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.04);color:#fff;font-size:13px;font-weight:800;cursor:pointer;transition:.2s}
                .gp-btn:hover{transform:translateY(-1px)}
                .gp-btn.primary{background:linear-gradient(135deg,var(--accent),var(--accent2));border-color:transparent}
                .gp-frame-wrap{position:relative;max-width:940px}
                .gp-frame{position:relative;aspect-ratio:16/9;border-radius:14px;overflow:hidden;background:#090909;border:1px solid rgba(255,255,255,.07)}
                .gp-frame iframe,.gp-frame img,.gp-frame-cover{display:block;width:100%;height:100%;border:0}
                .gp-frame iframe{background:#000}
                .gp-frame img{object-fit:cover;object-position:center}
                .gp-frame-cover{background:linear-gradient(180deg,#171717,#090909)}
                .gp-footer{display:flex;justify-content:space-between;align-items:center;gap:14px;flex-wrap:wrap;margin-top:14px;padding-top:14px;border-top:1px solid rgba(255,255,255,.07)}
                .gp-footer-group{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
                .gp-footer-copy{color:var(--muted);font-size:13px;line-height:1.6}
                .gp-feedback-btn{display:inline-flex;align-items:center;gap:8px;min-height:42px;padding:0 14px;border-radius:999px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.03);color:#fff;font-size:13px;font-weight:800;cursor:pointer;transition:.2s}
                .gp-feedback-btn:hover{transform:translateY(-1px);border-color:rgba(244,97,25,.4)}
                .gp-feedback-btn.active{background:rgba(244,97,25,.14);border-color:rgba(244,97,25,.55)}
                .gp-feedback-btn.report{background:rgba(255,255,255,.02)}
                .gp-feedback-count{color:#d8d8d8}
                .gp-feedback-note{color:var(--muted);font-size:12px}
                .gp-feedback-status{width:100%;color:#cfcfcf;font-size:12px}
                .gp-preview{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:18px;background:radial-gradient(circle at top,rgba(255,255,255,.05),transparent 34%),linear-gradient(180deg,rgba(0,0,0,.14),rgba(0,0,0,.72))}
                .gp-preview-card{display:flex;flex-direction:column;align-items:center;text-align:center;max-width:360px}
                .gp-preview-thumb{width:96px;height:96px;border-radius:22px;overflow:hidden;border:1px solid rgba(255,255,255,.12);background:#1a1a1a;box-shadow:0 14px 28px rgba(0,0,0,.22)}
                .gp-preview-thumb img,.gp-preview-thumb span{display:block;width:100%;height:100%}
                .gp-preview-thumb img{object-fit:cover}
                .gp-preview-thumb span{background:linear-gradient(180deg,#202020,#121212)}
                .gp-preview h2{margin:14px 0 4px;color:#fff;font-size:26px;font-weight:800;letter-spacing:-.03em}
                .gp-preview p{margin:0 0 18px;color:#c8c8c8;font-size:13px;line-height:1.6;max-width:320px}
                .gp-float{position:fixed;right:22px;bottom:calc(22px + env(safe-area-inset-bottom));z-index:80;display:none;align-items:center;justify-content:center;width:54px;height:54px;border:0;border-radius:999px;background:linear-gradient(135deg,var(--accent),var(--accent2));color:#fff;box-shadow:0 18px 36px rgba(0,0,0,.36);cursor:pointer}
                .gp-rotate{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;padding:24px;background:rgba(0,0,0,.88);color:#fff;text-align:center;opacity:0;pointer-events:none;transition:.2s;z-index:4}
                .gp-rotate.show{opacity:1;pointer-events:auto}
                .gp-rotate-card{max-width:320px}
                .gp-rotate-card i{display:inline-flex;align-items:center;justify-content:center;width:62px;height:62px;margin-bottom:14px;border-radius:999px;background:rgba(255,255,255,.08);font-size:24px}
                .gp-rotate-card h3{margin:0 0 8px;font-size:22px;font-weight:800}
                .gp-rotate-card p{margin:0;color:#cfcfcf;font-size:14px;line-height:1.6}
                .gp-side{display:flex;flex-direction:column;gap:16px;min-width:0}
                .gp-box{padding:18px;border-radius:18px;background:var(--panel);border:1px solid var(--border)}
                .gp-box h3{display:flex;align-items:center;gap:10px;margin:0 0 12px;color:#fff;font-size:18px;font-weight:800}
                .gp-box p{margin:0;color:var(--muted);font-size:13px;line-height:1.7;white-space:pre-line}
                .gp-list{display:flex;flex-wrap:wrap;gap:8px}
                .gp-chip{display:inline-flex;align-items:center;min-height:32px;padding:0 11px;border-radius:999px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.06);color:#d6d6d6;font-size:12px;font-weight:700}
                .gp-details{display:grid;grid-template-columns:minmax(0,1fr);gap:16px}
                .gp-meta-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px}
                .gp-box.compact h3{margin-bottom:10px;font-size:16px}
                .gp-related{position:sticky;top:96px}
                .gp-related-list{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}
                .gp-related-card{position:relative;display:block;overflow:hidden;border-radius:12px;border:1px solid rgba(255,255,255,.06);background:#0f0f0f;text-decoration:none;transition:.22s}
                .gp-related-card:hover{transform:translateY(-2px);border-color:rgba(255,255,255,.14);box-shadow:0 14px 24px rgba(0,0,0,.22)}
                .gp-related-thumb{position:relative;aspect-ratio:16/9;overflow:hidden;background:radial-gradient(circle at center,rgba(255,255,255,.08),transparent 58%),linear-gradient(160deg,#181818,#0b0b0b)}
                .gp-related-thumb img,.gp-related-thumb span{display:block;width:100%;height:100%}
                .gp-related-thumb img{position:relative;z-index:1;object-fit:contain;object-position:center;padding:2px;transition:transform .32s ease}
                .gp-related-thumb span{background:linear-gradient(180deg,#202020,#121212)}
                .gp-related-thumb::after{content:"";position:absolute;inset:0;background:linear-gradient(180deg,rgba(0,0,0,0) 18%,rgba(0,0,0,.18) 46%,rgba(0,0,0,.62) 74%,rgba(0,0,0,.96) 100%);opacity:.98}
                .gp-related-card:hover .gp-related-thumb img{transform:scale(1.02)}
                .gp-related-copy{position:absolute;left:0;right:0;bottom:0;z-index:2;padding:32px 10px 10px;background:linear-gradient(0deg,rgba(0,0,0,.96) 0%,rgba(0,0,0,.72) 46%,rgba(0,0,0,0) 100%);transform:translateY(18px);opacity:0;transition:.24s}
                .gp-related-copy strong{display:block;color:#fff;font-size:13px;line-height:1.35;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
                .gp-related-copy span{display:block;margin-top:3px;color:#b8b8b8;font-size:11px;line-height:1.4;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
                .gp-related-card:hover .gp-related-copy,.gp-related-card:focus-visible .gp-related-copy{transform:translateY(0);opacity:1}
                .gp-modal-backdrop{position:fixed;inset:0;z-index:95;display:flex;align-items:center;justify-content:center;padding:18px;background:rgba(0,0,0,.72);backdrop-filter:blur(6px)}
                .gp-modal{width:min(100%,520px);padding:22px;border-radius:20px;background:#111;border:1px solid rgba(255,255,255,.08);box-shadow:0 24px 60px rgba(0,0,0,.38)}
                .gp-modal-head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin-bottom:16px}
                .gp-modal-head h3{margin:0;color:#fff;font-size:24px;font-weight:800}
                .gp-modal-head p{margin:6px 0 0;color:var(--muted);font-size:13px;line-height:1.6}
                .gp-modal-close{width:40px;height:40px;border-radius:999px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.04);color:#fff;cursor:pointer}
                .gp-form-grid{display:grid;gap:14px}
                .gp-field label{display:block;margin-bottom:8px;color:#fff;font-size:12px;font-weight:800;letter-spacing:.08em;text-transform:uppercase}
                .gp-select,.gp-textarea{width:100%;border-radius:14px;border:1px solid rgba(255,255,255,.08);background:#171717;color:#fff}
                .gp-select{min-height:46px;padding:0 14px}
                .gp-textarea{min-height:140px;padding:14px;resize:vertical}
                .gp-modal-actions{display:flex;justify-content:flex-end;gap:10px;flex-wrap:wrap;margin-top:18px}
                .gp-empty{padding:52px 28px;text-align:center;color:var(--muted)}
                .float_button_visibility{display:flex!important}
                .fullscreen_manager{position:fixed!important;inset:0!important;width:100vw!important;height:100vh!important;z-index:70!important;border-radius:0!important;background:#000!important}
                @media (max-width:1199px){.gp-layout{grid-template-columns:1fr;justify-content:stretch}.gp-actions{justify-content:flex-start}.gp-frame-wrap{max-width:none}.gp-related{position:static}}
                @media (hover:none){.gp-related-copy{transform:translateY(0);opacity:1}}
                @media (max-width:900px){.gp-related-list{grid-template-columns:repeat(2,minmax(0,1fr))}}
                @media (max-width:767px){.gp-stage,.gp-box,.gp-modal{padding:16px}.gp-top,.gp-stage-head,.gp-footer{flex-direction:column;align-items:flex-start}.gp-title{font-size:26px}.gp-preview{padding:14px;align-items:center;justify-content:center;text-align:center}.gp-preview-card{width:100%;max-width:260px;justify-content:center;gap:12px}.gp-preview-thumb,.gp-preview p{display:none}.gp-preview h2{margin:0;font-size:20px}.gp-preview .gp-btn{min-width:160px}.gp-meta-grid{grid-template-columns:1fr}.gp-related-list{grid-template-columns:repeat(2,minmax(0,1fr))}.gp-modal-actions{justify-content:stretch}.gp-modal-actions .gp-btn{width:100%}.gp-float{right:16px;bottom:calc(16px + env(safe-area-inset-bottom));width:50px;height:50px}}
            `}</style>

            <div className="gp-page">
                <div className="gp-shell">
                    <div className="gp-top">
                        <div>
                            <Link to="/games" className="gp-link">
                                <i className="fa-solid fa-arrow-left"></i>
                                Back To Games
                            </Link>
                        </div>
                    </div>

                    {loading ? (
                        <div className="gp-panel gp-empty">Loading game...</div>
                    ) : game ? (
                        <div className="gp-layout">
                            <div className="gp-main">
                                <section className="gp-panel gp-stage">
                                    <div className="gp-stage-head">
                                        <div>
                                            <h1 className="gp-title">{game.title}</h1>
                                        </div>

                                        <div className="gp-actions">
                                            <button type="button" className="gp-btn" onClick={handleEnterFullscreen}>
                                                <i className="fa-solid fa-expand"></i>
                                                Full Screen
                                            </button>
                                            <button type="button" className="gp-btn" onClick={handleReload}>
                                                <i className="fa-solid fa-rotate-right"></i>
                                                Reload
                                            </button>
                                        </div>
                                    </div>

                                    <div className="gp-frame-wrap">
                                        <div id={playerIds.container} className="gp-frame">
                                            {started && game.embed ? (
                                                <iframe
                                                    id={playerIds.iframe}
                                                    src={game.embed+"?gd_sdk_referrer_url=https://cges.club/games/"+game.slug}
                                                    title={game.title}
                                                    allow="autoplay; fullscreen; gamepad *;"
                                                    allowFullScreen
                                                    referrerPolicy="strict-origin-when-cross-origin"
                                                />
                                            ) : preview ? (
                                                <img id={playerIds.image} src={preview} alt={game.title} />
                                            ) : (
                                                <div id={playerIds.image} className="gp-frame-cover" />
                                            )}

                                            {!started && isMobileOrTablet ? (
                                                <div className="gp-preview">
                                                    <div className="gp-preview-card">
                                                        <div className="gp-preview-thumb">
                                                            {preview ? <img src={preview} alt={game.title} /> : <span />}
                                                        </div>
                                                        <h2>{game.title}</h2>
                                                        <p>{game.company || "GameDistribution title"}</p>
                                                        <button type="button" id={playerIds.start} className="gp-btn primary" onClick={handleMobileLaunch}>
                                                            <i className="fa-solid fa-expand"></i>
                                                            Play Game
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : null}

                                            {!isMobileOrTablet ? (
                                                <div id={playerIds.rotateOverlay} className="gp-rotate">
                                                    <div className="gp-rotate-card">
                                                        <i className="fa-solid fa-mobile-screen-button"></i>
                                                        <h3>Rotate Device</h3>
                                                        <p>This game plays best in {orientation} mode. Rotate your device to continue.</p>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>

                                        <button type="button" id={playerIds.floatingExit} className="gp-float" aria-label="Exit full screen" onClick={handleExitFullscreen}>
                                            <i className="fa-solid fa-compress"></i>
                                        </button>
                                    </div>

                                    <div className="gp-footer">
                                        <div className="gp-footer-group">
                                            <button
                                                type="button"
                                                className={`gp-feedback-btn ${feedbackSummary.user_reaction === "like" ? "active" : ""}`}
                                                onClick={() => handleReaction("like")}
                                                disabled={feedbackBusy === "like"}
                                            >
                                                <i className="fa-regular fa-thumbs-up"></i>
                                                Like
                                                <span className="gp-feedback-count">{feedbackLoading ? "..." : feedbackSummary.like_count}</span>
                                            </button>
                                            <button
                                                type="button"
                                                className={`gp-feedback-btn ${feedbackSummary.user_reaction === "dislike" ? "active" : ""}`}
                                                onClick={() => handleReaction("dislike")}
                                                disabled={feedbackBusy === "dislike"}
                                            >
                                                <i className="fa-regular fa-thumbs-down"></i>
                                                Dislike
                                                <span className="gp-feedback-count">{feedbackLoading ? "..." : feedbackSummary.dislike_count}</span>
                                            </button>
                                            <button type="button" className="gp-feedback-btn report" onClick={handleOpenReport}>
                                                <i className="fa-regular fa-flag"></i>
                                                Report Game
                                            </button>
                                        </div>

                                        <div className="gp-footer-copy">
                                            <div className="gp-feedback-note">Share quick feedback or report issues with this game.</div>
                                        </div>

                                        {feedbackMessage ? <div className="gp-feedback-status">{feedbackMessage}</div> : null}
                                    </div>
                                </section>

                                <section className="gp-details">
                                    <div className="gp-box">
                                        <h3><i className="fa-solid fa-circle-info"></i> About This Game</h3>
                                        <p>{game.description || "No description available yet."}</p>
                                    </div>

                                    <div className="gp-box">
                                        <h3><i className="fa-solid fa-gamepad"></i> How To Play</h3>
                                        <p>{game.instruction || "Instructions are not available for this game."}</p>
                                    </div>

                                    <div className="gp-meta-grid">
                                        <div className="gp-box compact">
                                            <h3><i className="fa-solid fa-layer-group"></i> Categories</h3>
                                            <div className="gp-list">
                                                {(game.categories?.length ? game.categories : ["Not specified"]).map((item) => (
                                                    <span key={item} className="gp-chip">{item}</span>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="gp-box compact">
                                            <h3><i className="fa-solid fa-hashtag"></i> Tags</h3>
                                            <div className="gp-list">
                                                {(game.tags?.length ? game.tags : ["No tags"]).map((item) => (
                                                    <span key={item} className="gp-chip">{item}</span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            </div>

                            <aside className="gp-side">
                                <div className="gp-box gp-related">
                                    <h3><i className="fa-solid fa-fire"></i> Similar Games</h3>
                                    {similarLoading ? (
                                        <p>Loading similar games...</p>
                                    ) : similarGames.length ? (
                                        <div className="gp-related-list">
                                            {similarGames.map((item) => {
                                                const itemImage = buildGameImage(item);
                                                const itemSubtitle = item.categories?.slice(0, 2).join(" / ") || item.company || "GameDistribution title";

                                                return (
                                                    <Link key={item.game_id} to={`/games/${slugifyGame(item)}`} className="gp-related-card">
                                                        <div className="gp-related-thumb">
                                                            {itemImage ? <img src={itemImage} alt={item.title} loading="lazy" /> : <span />}
                                                        </div>
                                                        <div className="gp-related-copy">
                                                            <strong>{item.title}</strong>
                                                            <span>{itemSubtitle}</span>
                                                        </div>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p>No similar games found for this title yet.</p>
                                    )}
                                </div>
                            </aside>
                        </div>
                    ) : (
                        <div className="gp-panel gp-empty">Game not found.</div>
                    )}
                </div>
            </div>

            {isReportOpen ? (
                <div className="gp-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="gp-report-title" onClick={() => setIsReportOpen(false)}>
                    <div className="gp-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="gp-modal-head">
                            <div>
                                <h3 id="gp-report-title">Report This Game</h3>
                                <p>Tell us what went wrong and we will review the issue.</p>
                            </div>
                            <button type="button" className="gp-modal-close" aria-label="Close report popup" onClick={() => setIsReportOpen(false)}>
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        <form onSubmit={handleSubmitReport}>
                            <div className="gp-form-grid">
                                <div className="gp-field">
                                    <label htmlFor="gp-report-reason">Reason</label>
                                    <select
                                        id="gp-report-reason"
                                        className="gp-select"
                                        value={reportForm.reason}
                                        onChange={(event) => setReportForm((prev) => ({ ...prev, reason: event.target.value }))}
                                    >
                                        <option value="bug">Game bug or broken controls</option>
                                        <option value="loading">Game does not load</option>
                                        <option value="content">Wrong or inappropriate content</option>
                                        <option value="other">Other issue</option>
                                    </select>
                                </div>

                                <div className="gp-field">
                                    <label htmlFor="gp-report-details">Details</label>
                                    <textarea
                                        id="gp-report-details"
                                        className="gp-textarea"
                                        placeholder="Share device details, steps, or anything else that helps us reproduce the problem."
                                        value={reportForm.details}
                                        onChange={(event) => setReportForm((prev) => ({ ...prev, details: event.target.value }))}
                                    />
                                </div>
                            </div>

                            <div className="gp-modal-actions">
                                <button type="button" className="gp-btn" onClick={() => setIsReportOpen(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="gp-btn primary" disabled={reportSubmitting}>
                                    <i className="fa-regular fa-paper-plane"></i>
                                    {reportSubmitting ? "Submitting..." : "Submit Report"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}
        </>
    );
}
